import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import DataTable from "../../Table/DataTable";
import VerticalPoints from "../../Common/VerticalPoints/VerticalPoints";
import ConfirmModal from "../../Common/Modal/ConfirmModal";
import { openRightModal } from "../../../REDUX/actions/rightModal";
import { openCenteredModal } from "../../../REDUX/actions/centeredModal";
import {
  deleteAppCustomer,
  fetchAppCustomers,
} from "../../../REDUX/actions/appCustomers";
import { getModels } from "../../../REDUX/actions/model";
import { fetchCommercialOptions } from "../../../REDUX/actions/commercials";
import AppCustomerModal from "../AppCustomerModal";
import MagicLoginModal from "../MagicLoginModal";
import { formatEuro } from "../Projects/QuotePill";
import { prospectSourceOptions } from "../prospectSources";
import useCanSeePrices from "../../../hooks/useCanSeePrices";

// Client list filters (behind the DataTable "Filters" button).
const CLIENT_FILTERS = [
  {
    type: "select",
    label: "Commercial",
    name: "salesRep",
    placeholder: "Commercial",
    resetable: true,
    showClear: true,
  },
  {
    type: "select",
    label: "Contact",
    name: "contact",
    placeholder: "Contact",
    filter: true,
    resetable: true,
    showClear: true,
  },
  {
    type: "select",
    label: "Model",
    name: "model",
    placeholder: "Model",
    filter: true,
    resetable: true,
    showClear: true,
  },
  {
    type: "select",
    label: "Margin rate range",
    name: "marginRange",
    placeholder: "Margin rate range",
    resetable: true,
    showClear: true,
  },
];
// Same codes as the API (commande/utils/partnerOrder MARGIN_RANGES).
const MARGIN_RANGE_OPTIONS = [
  { name: "< 20 %", code: "lt20" },
  { name: "20 – 30 %", code: "20-30" },
  { name: "30 – 40 %", code: "30-40" },
  { name: "≥ 40 %", code: "gte40" },
];

const amount = (value) =>
  value === null || value === undefined ? "-" : formatEuro(value);

// One tab's list (role = "Prospect" | "Client"). The parent remounts this
// component when the tab or the store filter changes, so the DataTable
// refetches from page 1.
const AppCustomersTable = ({ role = "Prospect" }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("common");
  const { appCustomers, loading, pageCount, total, deleteLoading, scope } =
    useSelector((state) => state.appCustomersReducer);
  const { models } = useSelector((state) => state.modelReducer) ?? {};

  const [magicLoginCustomer, setMagicLoginCustomer] = useState(null);
  const [filters, setFilters] = useState({});
  const [commercialOptions, setCommercialOptions] = useState([]);

  const isProspect = role === "Prospect";
  // A store's commercial sees no margin (the API doesn't send it either).
  const canSeePrices = useCanSeePrices();
  const marginKeys = canSeePrices ? [] : ["Margin", "Margin rate", "marginRange"];

  // Client filter options: the store's commercials and the spa models.
  useEffect(() => {
    if (isProspect) return;
    const controller = new AbortController();
    if (!models?.length) {
      dispatch(getModels(i18n.language?.includes("en") ? "en" : "fr"));
    }
    fetchCommercialOptions(scope?.organizationId, controller.signal)
      .then(setCommercialOptions)
      .catch((error) => {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = (page, search, currentFilters, signal) => {
    dispatch(
      fetchAppCustomers(
        search,
        { ...(currentFilters ?? {}), role: { code: role } },
        page,
        signal,
      ),
    );
  };

  const options = (row) => (
    <VerticalPoints
      showLeft
      options={[
        {
          text: t("Edit"),
          callback: () =>
            dispatch(openRightModal(<AppCustomerModal customer={row} />)),
        },
        {
          text: t("QR Code"),
          callback: () => setMagicLoginCustomer(row),
        },
        {
          text: t("Delete"),
          red: true,
          loading: deleteLoading,
          callback: () =>
            dispatch(
              openCenteredModal(
                <ConfirmModal
                  label={`${row?.firstName ?? ""} ${row?.lastName ?? ""}`}
                  id={row?.id}
                  onConfirm={() => dispatch(deleteAppCustomer(row.id, t))}
                />,
              ),
            ),
        },
      ]}
    />
  );

  // Source codes are stored in English — display in the user's language.
  const contactColumn = {
    name: "Contact",
    render: (row) => (row?.contact ? t(`${row.contact}`) : "-"),
  };

  const prospectHeaders = [
    "Last name",
    "First name",
    "Phone number",
    "Email",
    "Address",
    "Contact",
    "",
  ];
  const prospectColumns = [
    { name: "Last name", render: (row) => row?.lastName ?? "-" },
    { name: "First name", render: (row) => row?.firstName ?? "-" },
    { name: "Phone number", render: (row) => row?.phoneNumber ?? "-" },
    { name: "Email", render: (row) => row?.email ?? "-" },
    {
      name: "Address",
      render: (row) =>
        [
          row?.address?.street,
          [row?.address?.zip, row?.address?.city?.name]
            .filter(Boolean)
            .join(" "),
          row?.address?.country?.name,
        ]
          .filter(Boolean)
          .join(", ") || "-",
    },
    contactColumn,
    { name: "options", render: options },
  ];

  // Client list (mockup "Client"): the figures of the client's order —
  // margin = the partner's (customer order HT − its purchase order HT).
  const clientHeaders = [
    "Last name",
    "First name",
    "Contact",
    "Model",
    "Order",
    "Revenue excl. VAT",
    "Margin",
    "Margin rate",
    "SAV tickets",
    "Commercial",
    "",
  ];
  const clientColumns = [
    { name: "Last name", render: (row) => row?.lastName ?? "-" },
    { name: "First name", render: (row) => row?.firstName ?? "-" },
    contactColumn,
    { name: "Model", render: (row) => row?.clientOrder?.model ?? "-" },
    { name: "Order", render: (row) => row?.clientOrder?.number ?? "-" },
    {
      name: "Revenue excl. VAT",
      render: (row) =>
        row?.clientOrder ? amount(row.clientOrder.totalHT) : "-",
    },
    { name: "Margin", render: (row) => amount(row?.clientOrder?.margin) },
    {
      name: "Margin rate",
      render: (row) =>
        row?.clientOrder?.marginRate === null ||
        row?.clientOrder?.marginRate === undefined
          ? "-"
          : `${row.clientOrder.marginRate.toLocaleString("fr-FR", {
              maximumFractionDigits: 1,
            })} %`,
    },
    {
      name: "SAV tickets",
      render: (row) => row?.clientOrder?.savTickets ?? "-",
    },
    {
      name: "Commercial",
      render: (row) => row?.clientOrder?.salesRep ?? "-",
    },
    { name: "options", render: options },
  ];

  const selectOptions = {
    salesRep: commercialOptions,
    contact: prospectSourceOptions(t),
    model: (models ?? [])
      .filter((model) => model.productType === 1)
      .map(({ name, code }) => ({ name, code })),
    marginRange: MARGIN_RANGE_OPTIONS,
  };

  return (
    <div>
      <DataTable
        title="General"
        total={total}
        headers={
          isProspect
            ? prospectHeaders
            : clientHeaders.filter((header) => !marginKeys.includes(header))
        }
        columns={
          isProspect
            ? prospectColumns
            : clientColumns.filter((column) => !marginKeys.includes(column.name))
        }
        data={appCustomers}
        loading={loading}
        totalPages={pageCount}
        fetchData={fetchData}
        // Prospects: search only (no setFilters → no Filters button).
        {...(isProspect
          ? { showRowDetails: false }
          : {
              filters,
              setFilters,
              filterElements: CLIENT_FILTERS.filter(
                (filter) => !marginKeys.includes(filter.name),
              ),
              selectOptions,
              view: "client",
            })}
        addNewItem={() =>
          dispatch(openRightModal(<AppCustomerModal defaultRole={role} />))
        }
        newItemTitle={isProspect ? t("Add a prospect") : t("Add a client")}
        noResultsMessage={t("No customers found.")}
      />
      {magicLoginCustomer && (
        <MagicLoginModal
          customer={magicLoginCustomer}
          onClose={() => setMagicLoginCustomer(null)}
        />
      )}
    </div>
  );
};

export default AppCustomersTable;
